// @flow
import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { List } from 'immutable';

import { Field, Formik, Form } from 'formik';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import InputAdornment from '@material-ui/core/InputAdornment';
import Typography from '@material-ui/core/Typography';
import { withStyles } from '@material-ui/core';

import LockIcon from '@material-ui/icons/LockOutlined';

import FormikTextField from 'components/Formik/TextField';
import QButton from 'components/QButton';

import { getPartnerAuthUris as getPartnerAuthUrisAction } from 'components/Dialogs/AccountDiscovery/actions';

import type { Institution } from 'data/institutions/types';

import { mkInstitutionLogin } from 'data/institutionLogins/types';
import type { InstitutionLogin } from 'data/institutionLogins/types';

import { HOSTNAME, PORT, PROTOCOL } from 'utils/webappConfig';
import tracker from 'utils/tracker';

import isAcme from 'isAcme';

import styles, { Credentials } from './styles';
import * as selectors from '../selectors';

const redirectUri = `${PROTOCOL}//${HOSTNAME}${PORT ? `:${PORT}` : ''}/partnerAuth`;

type Props = {
  institution: Institution,
  institutionLogin: ?InstitutionLogin,

  error: Object,

  onCancel: () => void,
  onSubmit: (InstitutionLogin, () => void, () => void) => void,

  cancelButtonLabel: string,
  submitButtonLabel: string,

  trackingProperties: Object, // for mixpanel tracking

  // from connect
  partnerAuthUris: List<string>,
  getPartnerAuthUris: (any) => void,

  // from styles
  classes: Object,
}

type State = {
  showPassword: boolean,

  errorMsgIsLarge: boolean,
  largeErrorMsgShown: boolean,

  partnerAuthInProgress: boolean,
  partnerAuthError: string,
}

class InstitutionLoginForm extends React.PureComponent<Props, State> {

  errorRef = null;
  form = null;
  inputRefs = [];

  constructor(props) {
    super(props);

    this.state = {
      showPassword: false,

      errorMsgIsLarge: false,
      largeErrorMsgShown: false,

      partnerAuthInProgress: false,
    };
  }

  componentDidMount() {
    const { institution } = this.props;

    // trigger call to getPartnerAuthUris if auth-type is PARTNER_AUTh
    const { fds } = institution;
    if (fds && fds.partnerAuth && !this.props.partnerAuthUris) {
      this.props.getPartnerAuthUris({ partnerAuth: fds.partnerAuth, redirectUri });
    }

    this.checkIfErrorMsgIsLarge();
  }

  componentDidUpdate(prevProps) {
    const { error, trackingProperties } = this.props;

    this.checkIfErrorMsgIsLarge();
    if (error && error !== prevProps.error) {
      tracker.track(tracker.events.addFIError, {
        ...trackingProperties,
        error_code: error.status,
        agg_status_code: error.statusCode,
      });
    }
  }

  checkIfErrorMsgIsLarge = () => {
    const errorMsgIsLarge = Boolean(this.errorRef && this.errorRef.scrollHeight > 50);

    if (this.state.errorMsgIsLarge !== errorMsgIsLarge) {
      this.setState({ errorMsgIsLarge });  // eslint-disable-line react/no-did-update-set-state
    }
  };

  // todo: should we switch to auth-type?
  isAuthTypePartnerAuth = () => Boolean(this.props.institution.fds && this.props.institution.fds.partnerAuth);

  handleCancel = () => {
    const { onCancel } = this.props;

    if (onCancel) {
      onCancel({
        trackingProperties: {
          step: 'credentials',
        },
      });
    }
  };

  handleClickError = () => this.setState((prevState) => ({ largeErrorMsgShown: !prevState.largeErrorMsgShown }));
  handleClickShowPassword = () => this.setState((prevState) => ({ showPassword: !prevState.showPassword }));

  handlePartnerAuthSignIn = () => {
    const { institution, partnerAuthUris } = this.props;
    const partnerAuthUri = partnerAuthUris.get(0);

    const child = window.open(partnerAuthUri, "windowname1", 'width=600, height=650');
    const pollTimer = window.setInterval(() => {
      try {
        if (child.document === undefined) {
          window.clearInterval(pollTimer);
          this.setState({ partnerAuthInProgress: false, partnerAuthError: null });
        }
        if (child.document.URL.indexOf(redirectUri) !== -1) {
          let partnerAuthError = null;

          const urlParams = new URLSearchParams(child.location.search);
          child.close();

          const token = urlParams.get('response_token');
          if (token) {
            const credentials =
              institution.fds &&
              institution.fds.partnerAuth &&
              institution.fds.partnerAuth.fields &&
              institution.fds.partnerAuth.fields.map((field) => ({
                key: field.key,
                value: field.displayLabel === 'response_token' ? token : '',
              }));
            this.doSubmit(credentials);
          } else {
            // const errorSource = urlParams.get('error_source');
            // const errorCode = urlParams.get('error_code');
            const errorDescription = urlParams.get('error_description');
            if (errorDescription) {
              partnerAuthError = atob(errorDescription);
            }
            else {
              partnerAuthError = "Unexpected error";
            }
          }

          window.clearInterval(pollTimer);
          this.setState({ partnerAuthInProgress: false, partnerAuthError });
        }
      } catch(e) {
        if (e.name === 'SecurityError') {
          // until we are redirected back to our domain, this exception is expected
        } else {
          // don't really expect this to happen
          window.clearInterval(pollTimer);
          this.setState({ partnerAuthInProgress: false, partnerAuthError: `Unexpected Error: ${e.name}` });
        }
      }
    }, 100);
    this.setState({ partnerAuthInProgress: true, partnerAuthError: null });
  };

  handleSubmit = (values, _actions) => {
    const { institution } = this.props;
    const credentials = institution.fds && institution.fds.formFields &&
      institution.fds.formFields.map((field) => ({
        key: field.key,
        value: values[field.key],
      }));
    this.doSubmit(credentials);
  };

  doSubmit = (credentials) => {
    if (this.props.onSubmit) {
      // todo: should we create a name around the username vs institution name (or both)?
      const institutionLogin = this.props.institutionLogin
        || mkInstitutionLogin({ institutionId: this.props.institution.id, name: this.props.institution.name });

      this.props.onSubmit(
        institutionLogin.set('credentials', credentials),
        () => { this.form && this.form.setSubmitting(false); },
        () => { this.form && this.form.setSubmitting(false); }
      );
    }
  };

  pushRef = (node) => {
    this.inputRefs.push(node);
  };

  renderField = (field, index, formikBag, classes) => {
    const isPasswordField = field.type === 'PASSWORD';

    return (
      <div className={classes.field} key={field.key}>
        <Field
          component={FormikTextField}
          fullWidth
          label={field.displayLabel}
          name={field.key}
          placeholder="answer"
          variant="outlined"
          type={!isPasswordField || this.state.showPassword ? 'text' : 'password'}
          autoComplete={isPasswordField ? 'new-password' : null}
          InputProps={{
            id: `field#${index}`,
            autoFocus: index === 0,
            endAdornment: (
              <InputAdornment position="end">
                <LockIcon className={classes.lock}/>
              </InputAdornment>
            ),
            // inputProps: {
            //   ref: this.pushRef,
            // },
          }}
          InputLabelProps={{
            classes: {
              shrink: classes.whiteBack,
            },
          }}
        />
      </div>
    );
  };

  renderErrorMsg = (errorMsg) => {
    const { classes } = this.props;
    const { errorMsgIsLarge, largeErrorMsgShown } = this.state;

    return (
      <div
        className={classes.errorBox}
      >
        <div className={classes.errorView} style={{ maxHeight: !largeErrorMsgShown && 38 }} ref={(node) => {this.errorRef = node;}}>
          <Typography variant="caption">
            {errorMsg}
          </Typography>
          {errorMsgIsLarge &&
          <div className={classes.errorButtonWrapper}>
            <Button
              classes={{ text: classes.errorText }}
              className={classes.errorButton}
              onClick={this.handleClickError}
            >
              ...
              <span className={classes.expandHighlight}>
                {largeErrorMsgShown ? 'READ LESS' : 'READ MORE'}
              </span>
            </Button>
          </div>
          }
        </div>
      </div>
    );
  };

  renderCredentialsForm = () => {
    const {
      cancelButtonLabel,
      classes,
      error,
      institution,
      submitButtonLabel,
    } = this.props;

    return (
      <Formik
        initialValues={{}}
        onSubmit={this.handleSubmit}
        ref={(form) => { this.form = form; }}
        validate={(values) => {
          const validateResult = institution.fds.formFields.reduce((result, field) =>
            ({
              ...result,
              ...(!values[field.key] ? {[field.key]: 'Required'} : null),
            }),
          {});
          return validateResult;
        }}
        render={(formikBag) => {
          const {handleSubmit: formikHandleSubmit, isSubmitting} = formikBag;
          return (
            <Form className={classes.form} onSubmit={formikHandleSubmit}>
              { institution.fds ?
                <div className={classes.formContent}>
                  {error && this.renderErrorMsg(error.helpText)}
                  <Credentials>
                    {institution.fds && institution.fds.formFields &&
                     institution.fds.formFields.map((field, index) => this.renderField(field, index, formikBag, classes))}
                  </Credentials>
                  <FormControlLabel
                    label="Show Password"
                    classes={{label: classes.checkLabel, root: classes.checkBox}}
                    control={
                      <Checkbox
                        checked={this.state.showPassword}
                        onChange={this.handleClickShowPassword}
                        value="Show Password"
                      />}
                  />
                </div> :
                <div className={classes.notAvailable}>This Bank is not available right now</div>
              }
              <div className={classes.formActions}>
                <QButton
                  id="backButton"
                  variant="text"
                  style={{ marginRight: 12 }}
                  onClick={this.handleCancel}
                >
                  {cancelButtonLabel}
                </QButton>
                { institution.fds &&
                  <QButton
                    id="connectButton"
                    type="submit"
                    variant="contained"
                    width={180}
                    disabled={isSubmitting}
                  >
                    {submitButtonLabel}
                  </QButton>
                }
              </div>
            </Form>
          );
        }}
      />
    );
  };

  renderPartnerAuth = () => {
    const { classes, error, partnerAuthUris } = this.props;
    const { partnerAuthError, partnerAuthInProgress } = this.state;

    if (!partnerAuthUris) {
      return (
        <div className={classes.isBusyContainer} align="center">
          <CircularProgress size={130} className={classes.progress}/>
        </div>
      );
    }

    const errorMsg = (error && error.helpText) || partnerAuthError;

    return (
      <div className={classes.partnerAuthIntro}>
        <Typography variant="subtitle1">
          Authorization access required
        </Typography>
        {errorMsg && this.renderErrorMsg(errorMsg)}
        <ul className={classes.partnerAuthList}>
          <li>
            <Typography variant="body2" className={classes.partnerListItem}>
              Sign in to Capital One through the secure browsing window.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" className={classes.partnerListItem}>
              When prompted, SELECT ALL YOUR ACCOUNTS.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" className={classes.partnerListItem}>
              {`You'll be able to hide accounts in ${isAcme ? 'Simplifi' : 'Quicken'} after a secure connection is established.`}
              <span className={classes.partnerAuthMoreInfoContainer}>
                <a href="https://www.quicken.com/support/changes-your-capital-one-account-services-quicken/" target="PartnerAuthFAQ">More Info</a>
              </span>
            </Typography>
          </li>
        </ul>
        <div className={classes.partnerAuthActions}>
          <QButton variant="text" style={{ marginRight: 12 }} width={104} onClick={this.props.onCancel}>
            Go Back
          </QButton>
          <QButton onClick={this.handlePartnerAuthSignIn} variant="contained" width={104}>
            {!partnerAuthInProgress ? 'Sign In' : <CircularProgress size={24}/>}
          </QButton>
        </div>
      </div>
    );
  };

  render() {
    return this.isAuthTypePartnerAuth() ? this.renderPartnerAuth() : this.renderCredentialsForm();
  }
}

InstitutionLoginForm.defaultProps = {
  cancelButtonLabel: 'Cancel',
  submitButtonLabel: 'Submit',
};

function mapStateToProps(state) {
  return {
    partnerAuthUris: selectors.getPartnerAuthUris(state),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    getPartnerAuthUris: (authPartner) => dispatch(getPartnerAuthUrisAction(authPartner)),
  };
}

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(InstitutionLoginForm);
